import { useState } from "react";
import {
  DEFAULT_PX_PER_MM,
  MIN_PX_PER_MM,
  MAX_PX_PER_MM,
  REFERENCE_OBJECTS,
  clampPxPerMm,
  savePxPerMm,
  type ReferenceObject,
} from "@/lib/screen-calibration";
import { CARD_SURFACE } from "@/components/page-shell";
import { cn } from "@/lib/utils";

/**
 * Calibrate the screen against something in the user's pocket.
 *
 * Everything below this in the identification flow is drawn from millimetre figures in
 * src/lib/tick-id.ts, so it is only honest if a millimetre on screen is a millimetre.
 *
 * A small tool, set compactly: the object picker, the slider and the two actions on one
 * side, the outline to hold the coin against on the other.
 */
export function SizeRuler({
  pxPerMm,
  onChange,
}: {
  pxPerMm: number;
  onChange: (value: number) => void;
}) {
  const [reference, setReference] = useState<ReferenceObject>(REFERENCE_OBJECTS[0]);
  const [saved, setSaved] = useState(false);

  const width = reference.mm * pxPerMm;
  const height = (reference.heightMm ?? reference.mm) * pxPerMm;

  return (
    <div className={cn(CARD_SURFACE, "p-4 sm:p-5 md:max-w-3xl")}>
      <div className="grid gap-x-8 gap-y-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:items-center">
        <div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Pick something you have to hand, hold it against the screen and drag the slider until
            the outline matches it. Every tick on this page is then drawn at its real size.
          </p>

          <div
            role="group"
            aria-label="Object to measure against"
            className="mt-3 grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap"
          >
            {REFERENCE_OBJECTS.map((r) => {
              const selected = r.id === reference.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setReference(r)}
                  aria-pressed={selected}
                  className={cn(
                    "min-h-11 rounded-sm border px-3 py-2 text-sm text-foreground transition-colors",
                    selected
                      ? "border-foreground bg-secondary font-semibold ring-1 ring-inset ring-foreground"
                      : "border-border bg-card hover:border-foreground/50",
                  )}
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          <label htmlFor="calibration" className="mt-4 block text-sm font-semibold text-foreground">
            Size on your screen
          </label>
          <input
            id="calibration"
            type="range"
            min={MIN_PX_PER_MM}
            max={MAX_PX_PER_MM}
            step={0.01}
            value={pxPerMm}
            onChange={(e) => {
              setSaved(false);
              onChange(clampPxPerMm(Number.parseFloat(e.target.value)));
            }}
            className="mt-1 h-11 w-full accent-primary"
          />

          <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-2">
            <button
              type="button"
              aria-live="polite"
              onClick={() => {
                savePxPerMm(pxPerMm);
                setSaved(true);
              }}
              className="inline-flex min-h-11 items-center rounded-sm border border-foreground/30 px-4 text-sm font-semibold text-foreground hover:border-foreground"
            >
              {saved ? "Remembered" : "Remember this screen"}
            </button>
            <button
              type="button"
              onClick={() => {
                setSaved(false);
                onChange(DEFAULT_PX_PER_MM);
              }}
              className="link min-h-11 text-sm font-semibold"
            >
              Reset
            </button>
          </div>
        </div>

        <div className="grid min-h-40 place-items-center overflow-x-auto py-2">
          <div
            aria-hidden
            style={{ width, height }}
            className={cn(
              "border-2 border-dashed border-primary bg-primary/5",
              reference.shape === "circle" ? "rounded-full" : "rounded-md",
            )}
          />
        </div>
      </div>
    </div>
  );
}
